// Единый слой хранения истории генераций: массив метаданных в localStorage + блобы в IndexedDB.
//
// До этого модуля девять компонентов носили по своей копии этого кода. Копии разошлись:
// у каждого инструмента своя тройка (имя базы, имя store, версия), и это НЕ случайность —
// это состояние, которое уже лежит в браузерах живых пользователей.
//
// ponytail: версии баз намеренно разные — у семи пресетов их менять нельзя, там лежат живые
// записи пользователей (слепок реального профиля: 70, 12, 7, 5, 10, 14, 7 блобов); выравнивать
// такие только через явный upgrade-путь с переносом записей. У `vector-ai` версия сознательно
// поднята с 1 до 2 — это не выравнивание, а починка: store 'images' там не существовал вообще
// и терять было нечего (подробности на самой строке конфига).
//
// Правила работы с версией (цена ошибки — молча стёртая история):
// - открытие существующей базы с версией НИЖЕ текущей бросает VersionError;
// - открытие с версией ВЫШЕ запускает onupgradeneeded, где неверный обработчик сносит записи;
// - поэтому версия берётся из конфига и НИКОГДА не выдумывается на месте.
//
// Голого `indexedDB.open(name)` без версии в этом модуле нет ни одного — и не должно появиться.
// Именно он был источником живого бага: открытие без версии создаёт базу версии 1 с нулём
// object store. Дальше запись открывает ту же версию 1 → onupgradeneeded не срабатывает →
// store так и не создаётся → каждая запись падает на «Object store does not exist». Навсегда.
// Пресеты с версией 2 это спасало случайно (запись апгрейдила пустую v1), `vector-ai` с версией 1
// спасать было нечем. Поэтому и чтение, и запись открывают базу одинаково: openDb(cfg).

import { getProxiedUrl } from './proxyUrl';

// Тройки взяты ровно те, что уже используются компонентами. Ничего не «унифицировано».
export const HISTORY_CONFIGS = {
  'replace-model':      { historyKey: 'ai-model-history',           dbName: 'replace-model-images-db',      store: 'images', version: 2 },
  'replace-background': { historyKey: 'ai-generation-history',      dbName: 'replace-background-images-db', store: 'images', version: 2 },
  'photosession':       { historyKey: 'ai-photosession-history',    dbName: 'photosession-images-db',       store: 'images', version: 2 },
  'photo-generation':   { historyKey: 'ai-photogeneration-history', dbName: 'photo-generation-images-db',   store: 'images', version: 2 },
  // vector-ai: единственная тройка, отличающаяся от той, что была в компоненте (там v1).
  // Слепок реального профиля: база vector-ai-images-db существует, версия 1, список object store
  // ПУСТ, а в localStorage при этом 12 записей — то есть блобы Векторной AI не сохранялись
  // никогда. Версия 2 впервые запускает onupgradeneeded и создаёт недостающий store 'images'.
  // Данные при этом не рискуют: терять в пустой базе нечего, а localStorage upgrade не трогает.
  'vector-ai':          { historyKey: 'ai-vector-ai-history',       dbName: 'vector-ai-images-db',          store: 'images', version: 2 },
  'photo-to-video':     { historyKey: 'ai-video-history',           dbName: 'photo-to-video-db',            store: 'videos', version: 2 },
  'auto-product-card':  { historyKey: 'ai-auto-product-history',    dbName: 'auto-product-images-db',       store: 'images', version: 2 },
  // ВНИМАНИЕ (video-merge): версия 1 здесь стоит по ТЗ, но реальности она не соответствует.
  // У VideoMerge.js фиксированной версии нет вообще: ensureIndexedDBStore() (VideoMerge.js:369)
  // открывает базу БЕЗ версии, а если store не найден — переоткрывает на db.version + 1;
  // единица используется только при самом первом создании. Слепок реального профиля это
  // подтверждает: video-merge-db там версии 2 с семью записями. Открытие такой базы с версией 1
  // бросит VersionError. Сейчас это ни на что не влияет — VideoMerge на history.js не переведён
  // и вне объёма плана (§0), этот конфиг никто не читает. Перед первым использованием
  // поставить сюда 2 (или снимать версию с самой базы), иначе история слияния видео отвалится.
  'video-merge':        { historyKey: 'ai-video-merge-history',     dbName: 'video-merge-db',               store: 'videos', version: 1 },
  // retouch: новая база, живых записей нет — версия 2 как у остальных. В блоб ложится склейка
  // с оригиналом (finalize в jobs.js), в urls — токен прокси на сырой ответ KIE.
  'retouch':            { historyKey: 'ai-retouch-history',         dbName: 'retouch-images-db',            store: 'images', version: 2 }
};

export const getHistoryConfig = (presetId) => HISTORY_CONFIGS[presetId] || null;

// SVG обычно занимает единицы килобайт; слишком большие data URL в localStorage не кладём (квота ~5MB)
export const MAX_INLINE_URL_LENGTH = 200000;

const BLOB_FETCH_TIMEOUT_MS = 60000;

const isDataUrl = (url) => typeof url === 'string' && url.startsWith('data:');

// data URL → Blob без fetch: fetch(data:) может блокироваться CSP (connect-src)
export const dataUrlToBlob = (dataUrl) => {
  const match = /^data:([^;,]+)(;base64)?,(.*)$/.exec(dataUrl || '');
  if (!match) return null;
  const mime = match[1] || 'image/svg+xml';
  try {
    if (match[2]) {
      const binary = atob(match[3]);
      const bytes = new Uint8Array(binary.length);
      for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
      return new Blob([bytes], { type: mime });
    }
    return new Blob([decodeURIComponent(match[3])], { type: mime });
  } catch (e) {
    return null;
  }
};

// Единственная точка открытия базы — и для чтения, и для записи. Всегда с версией из конфига
// и всегда с onupgradeneeded: открытие без версии создаёт базу без object store (см. шапку файла).
//
// Соединение живёт ровно одну операцию: вызывающий обязан закрыть его (db.close()) в finally.
// Открытая навечно вкладка блокирует апгрейд у всех остальных.
//
// Про таймаут. Апгрейд блокируется, если базу держит вкладка со старым бандлом. По спеке событие
// `blocked` получает только ПЕРВЫЙ запрос в очереди; запросы №2…№N не получают вообще ничего и
// висят молча. Без таймаута такой промис не резолвится никогда: `await saveBlob(...)` в
// addToHistory зависает, `history.append` после него не выполняется — оплаченная генерация не
// попадает в историю, а `finally { setLoading(false) }` не срабатывает и кнопка залипает.
// С таймаутом худший случай деградирует до старого поведения: saveBlob бросил → catch →
// append выполнился → запись в localStorage есть, нет только блоба.
const OPEN_TIMEOUT_MS = 8000;

export function openDb(cfg) {
  return new Promise((resolve, reject) => {
    if (typeof indexedDB === 'undefined') {
      reject(new Error('IndexedDB is not available'));
      return;
    }
    let timer = null;
    let settled = false;
    // Возвращает false, если промис уже разрешён — тогда открывшееся соединение надо закрыть.
    const settle = (fn, arg) => {
      if (settled) return false;
      settled = true;
      clearTimeout(timer);
      fn(arg);
      return true;
    };
    timer = setTimeout(
      () => settle(reject, new Error(`IndexedDB '${cfg.dbName}' open timed out`)),
      OPEN_TIMEOUT_MS
    );

    const request = indexedDB.open(cfg.dbName, cfg.version);
    request.onerror = () => settle(reject, request.error);
    request.onblocked = () => settle(reject, new Error(`IndexedDB '${cfg.dbName}' is blocked`));
    request.onupgradeneeded = (event) => {
      const db = event.target.result;
      // Только создание отсутствующего store. Ничего не удаляем и не пересоздаём.
      if (!db.objectStoreNames.contains(cfg.store)) {
        const objectStore = db.createObjectStore(cfg.store, { keyPath: 'id' });
        objectStore.createIndex('timestamp', 'timestamp', { unique: false });
      }
    };
    request.onsuccess = () => {
      const db = request.result;
      // Другая вкладка пошла на апгрейд — отпускаем базу, иначе блокируем её насмерть.
      db.onversionchange = () => {
        try { db.close(); } catch (e) {}
      };
      if (!settle(resolve, db)) {
        // Опоздали: промис уже отклонён по таймауту. Соединение никому не нужно — закрываем.
        try { db.close(); } catch (e) {}
      }
    };
  });
}

async function fetchSourceBlob(sourceUrl) {
  if (isDataUrl(sourceUrl)) {
    return dataUrlToBlob(sourceUrl);
  }
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), BLOB_FETCH_TIMEOUT_MS);
  try {
    const response = await fetch(getProxiedUrl(sourceUrl), { signal: controller.signal });
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    return await response.blob();
  } finally {
    clearTimeout(timeoutId);
  }
}

// Скачать источник и положить блоб в IndexedDB. Бросает — вызывающий решает, что делать.
export async function saveBlob(cfg, id, sourceUrl) {
  const blob = await fetchSourceBlob(sourceUrl);
  if (!blob || blob.size === 0) {
    throw new Error('Blob is empty');
  }
  const record = { id, blob, timestamp: Date.now() };
  // data URL рядом с блобом не дублируем — это тот же байт-в-байт контент, только в 1.37 раза толще
  if (!isDataUrl(sourceUrl)) {
    record.url = sourceUrl;
  }
  const db = await openDb(cfg);
  try {
    if (!db.objectStoreNames.contains(cfg.store)) {
      throw new Error(`Object store '${cfg.store}' does not exist`);
    }
    // Резолвим на oncomplete, а не на putRequest.onsuccess: put успевает «удаться», а транзакция
    // потом отваливается на коммите (например, по квоте). На onsuccess вызывающий уже считал бы
    // блоб сохранённым.
    await new Promise((resolve, reject) => {
      const transaction = db.transaction([cfg.store], 'readwrite');
      transaction.objectStore(cfg.store).put(record);
      transaction.oncomplete = () => resolve();
      transaction.onerror = () => reject(transaction.error || new Error('Transaction failed'));
      transaction.onabort = () => reject(transaction.error || new Error('Transaction aborted'));
    });
  } finally {
    try { db.close(); } catch (e) {}
  }
}

// Блоб из IndexedDB или null. Никогда не бросает: отсутствие кэша — штатная ситуация.
// Открывает базу тем же openDb, что и запись: чтение не должно оставлять после себя базу
// без object store — на этом и держался баг vector-ai.
export async function loadBlob(cfg, id) {
  let db = null;
  try {
    db = await openDb(cfg);
    if (!db.objectStoreNames.contains(cfg.store)) {
      return null;
    }
    // Blob переживает close(): это снимок, а не курсор в открытую базу.
    return await new Promise((resolve) => {
      const getRequest = db.transaction([cfg.store], 'readonly').objectStore(cfg.store).get(id);
      getRequest.onsuccess = () => {
        const record = getRequest.result;
        resolve(record && record.blob ? record.blob : null);
      };
      getRequest.onerror = () => resolve(null);
    });
  } catch (e) {
    return null;
  } finally {
    if (db) {
      try { db.close(); } catch (e) {}
    }
  }
}

// object URL для показа или null. Никогда не бросает.
export async function loadBlobUrl(cfg, id) {
  const blob = await loadBlob(cfg, id);
  return blob ? URL.createObjectURL(blob) : null;
}

// Массив истории из localStorage. При любой ошибке — []. SSR тоже сюда попадает.
//
// ponytail: нормализация «валидный JSON, но не массив» → [] сделана намеренно — вызывающие
// вправе рассчитывать на массив и не проверять тип. Цена: вместе с эффектом персиста в компоненте
// это значение уничтожается. Под ключом лежит, скажем, объект → read вернул [] → эффект записал
// [] обратно, сырое значение потеряно. Находку 4 этот случай обходит: hasStored при непустом
// значении true, и эффект пишет. Старый код возвращал распарсенное как есть и такие данные
// переживали цикл. Достижимость низкая: под ключ должен был кто-то положить не-массив руками или
// чужим кодом. Чинить (возвращать [] наружу, но не трогать хранилище) только если всплывёт живьём.
export function read(cfg) {
  try {
    const saved = localStorage.getItem(cfg.historyKey);
    const parsed = saved ? JSON.parse(saved) : [];
    return Array.isArray(parsed) ? parsed : [];
  } catch (e) {
    return [];
  }
}

// Есть ли вообще запись под ключом (пустой массив — тоже «есть»: бэкфилл уже отработал).
// ВАЖНО для вызывающих: пустой массив под ключом означает «серверный бэкфилл отработал и вернул
// пусто». Поэтому эффект персиста в компоненте НЕ должен писать `[]` на первом визите — иначе
// hasStored станет true до того, как бэкфилл вообще состоялся, и при упавшем запросе
// (офлайн / 502 / abort) серверная история не подтянется уже никогда.
export function hasStored(cfg) {
  try {
    return localStorage.getItem(cfg.historyKey) !== null;
  } catch (e) {
    return false;
  }
}

export function write(cfg, items) {
  try {
    localStorage.setItem(cfg.historyKey, JSON.stringify(items));
    return true;
  } catch (e) {
    return false;
  }
}

// Дописать запись первой. Возвращает новый массив — его же кладём в стейт компонента.
//
// ponytail: неудача записи (переполненная квота localStorage) наружу не видна — append возвращает
// новый массив одинаково и при успехе, и при провале write, так что компонент показывает запись,
// которой после перезагрузки не будет. Потолок известный: чинится сменой контракта возврата
// (отдавать { items, persisted } либо бросать). Контракт заморожен до конца волны 2 — против
// текущих сигнатур прямо сейчас пишется jobs.js (PR3). Вернуться сразу после.
export function append(cfg, item) {
  const next = [item, ...read(cfg)];
  write(cfg, next);
  return next;
}

// localId (timestamp) из token в public_url: .../proxy-image?token=img_1700000000000_abc
export function extractLocalIdFromToken(publicUrl) {
  if (!publicUrl) return null;
  const match = String(publicUrl).match(/token=(?:vid|img)_(\d+)_/);
  if (match && match[1]) {
    return parseInt(match[1], 10);
  }
  return null;
}

// Догрузка истории с бэкенда, когда локального кэша нет. null = не получилось, кэш не трогаем.
export async function backfillFromServer(cfg, userId, model, options = {}) {
  if (!userId) return null;
  try {
    const resp = await fetch('/api/n8n-proxy', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        endpoint: '/user/history/images',
        data: { user_id: userId, page: 1, limit: 20, model }
      }),
      signal: options.signal
    });
    const data = await resp.json();
    if (!resp.ok || !data || !data.ok || !Array.isArray(data.data)) {
      return null;
    }
    const mapped = data.data.map((x) => ({
      id: extractLocalIdFromToken(x.public_url) || x.id || Date.now(),
      urls: [x.public_url].filter(Boolean),
      prompt: x.prompt || '',
      model: x.kie_model || null,
      timestamp: x.created_at
        ? new Date(x.created_at).toLocaleString('ru-RU')
        : new Date().toLocaleString('ru-RU')
    }));
    // Мерж, а не затирание. Пока запрос был в полёте, пользователь мог успеть сгенерировать —
    // append уже записал результат в localStorage. Слепое write(mapped) стёрло бы свежую запись,
    // а её блоб остался бы сиротой в IndexedDB. Локальные записи, которых нет в ответе сервера,
    // встают в начало: они новее всего, что успел отдать сервер.
    const serverIds = new Set(mapped.map((x) => x.id));
    const localOnly = read(cfg).filter((x) => x && !serverIds.has(x.id));
    const merged = [...localOnly, ...mapped];
    write(cfg, merged);
    return merged;
  } catch (e) {
    return null;
  }
}

// Подтянуть в IndexedDB блобы тех записей, которых там ещё нет. Ошибки по одной записи глотаем.
export async function preloadMissingBlobs(cfg, items) {
  for (const item of items || []) {
    if (!item || !item.id) continue;
    const existing = await loadBlob(cfg, item.id);
    if (existing) continue;
    const sourceUrl = (Array.isArray(item.urls) && item.urls[0]) || item.url;
    if (!sourceUrl) continue;
    try {
      await saveBlob(cfg, item.id, sourceUrl);
    } catch (e) {
    }
  }
}
